import { api } from './api';

export interface PreCannedReportFilter {
  id: string;
  label: string;
  type: 'text' | 'select' | 'date' | string;
  default?: unknown;
  disabled?: boolean;
  placeholder?: string | null;
  help_text?: string | null;
  options?: Array<{
    label?: string;
    value?: unknown;
    [key: string]: unknown;
  }>;
}

export interface PreCannedReportTemplateSummary {
  id: string;
  title: string;
  description: string;
  version: string;
  filters: PreCannedReportFilter[];
  defaults: Record<string, unknown>;
}

export interface PreCannedReportListResponse {
  templates: PreCannedReportTemplateSummary[];
}

export interface PreCannedPeriodResolution {
  tool_period: string;
  period_label: string;
  period: string;
  inventory_fiscal_period?: string;
}

export type PreCannedReportTemplate = Record<string, unknown>;

export interface PreCannedReportMetadata extends Record<string, unknown> {
  last_updated_at?: string | null;
  data_freshness?: 'fresh' | 'stale' | string;
  source?: 'sac' | 'database_fallback' | string;
  fallback_reason?: string | null;
}

export interface PreCannedReportResult extends Record<string, unknown> {
  meta?: PreCannedReportMetadata;
}

export interface SmartMorningChatResponse {
  status: 'answered' | 'clarification' | 'redirect' | 'unavailable';
  message: string;
  thread_id?: string | null;
  run_id?: string | null;
}

export type DashboardFeedbackRating = 'thumbs_up' | 'thumbs_down';

export interface StoredDashboardFeedback {
  rating: DashboardFeedbackRating;
  text: string | null;
  submitted_at: string;
  submitted_by?: string;
}

export interface PreCannedDashboardFeedbackRequest {
  templateId: string;
  dashboardId: 'finance' | 'commercial' | 'operations';
  parameters: Record<string, unknown>;
  rating: DashboardFeedbackRating;
  text: string;
}

export interface PreCannedDashboardFeedbackResponse {
  template_id: string;
  dashboard_id: string;
  cache_key: string;
  feedback: StoredDashboardFeedback;
}

export type PreCannedDashboardFeedbackResult =
  | { status: 'submitted'; feedback: StoredDashboardFeedback }
  | { status: 'skipped'; reason: 'invalid_request' | 'missing_text' | 'text_too_long' }
  | { status: 'failed'; error: unknown };

const BASE = '/api/v1/pre-canned-reports';
const HYDRATION_POLL_INTERVAL_MS = 2000;
const HYDRATION_MAX_WAIT_MS = 10 * 60 * 1000;
export const PENDING_PRE_CANNED_SAP_REFRESH_KEY = 'smartchg:pre-canned-reports:pending-sap-refresh';
const SAP_RECONNECT_ERROR_CODES = new Set([
  'SAP_NOT_CONNECTED',
  'SAP_REFRESH_FAILED',
  'SAP_REFRESH_TOKEN_EXPIRED',
  'SAP_TOKEN_EXPIRED',
]);
const MAX_DASHBOARD_FEEDBACK_TEXT_LENGTH = 500;

export interface PreCannedHydrationJobCreated {
  run_id: string;
  status: string;
  template_id?: string;
  result?: PreCannedReportResult | null;
  error_code?: string | null;
  completed_at?: string | null;
  cache_hit?: boolean;
  execution?: string;
}

export interface PreCannedHydrationJobStatus extends PreCannedHydrationJobCreated {
  result?: PreCannedReportResult | null;
  error?: string | null;
  error_code?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
}

type HydrationProgressHandler = (result: PreCannedReportResult) => void;

export interface PreCannedHydrationOptions {
  forceRefresh?: boolean;
}

export class PreCannedSapReconnectRequiredError extends Error {
  public readonly errorCode: string;

  constructor(errorCode: string, message = 'SAP Datasphere needs to be reconnected.') {
    super(message);
    this.name = 'PreCannedSapReconnectRequiredError';
    this.errorCode = errorCode;
  }
}

function readErrorCode(value: unknown): string | null {
  if (typeof value === 'string') {
    try {
      return readErrorCode(JSON.parse(value));
    } catch {
      const match = value.match(/\bSAP_[A-Z0-9_]+\b/);
      return match?.[0] ?? null;
    }
  }
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const direct = record.error_code ?? record.errorCode;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  return (
    readErrorCode(record.detail) ||
    readErrorCode(record.error) ||
    readErrorCode(record.message) ||
    readErrorCode(record.response)
  );
}

function readErrorMessage(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return readErrorMessage(JSON.parse(trimmed)) ?? trimmed;
    } catch {
      return trimmed;
    }
  }
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const direct = record.message ?? record.detail ?? record.error;
  if (typeof direct === 'string' && direct.trim()) return direct.trim();
  return readErrorMessage(record.detail) || readErrorMessage(record.error);
}

export function getPreCannedSapReconnectErrorCode(error: unknown): string | null {
  if (error instanceof PreCannedSapReconnectRequiredError) return error.errorCode;
  const maybe = error as { response?: { data?: unknown; status?: number }; data?: unknown; message?: unknown };
  const code = readErrorCode(maybe?.response?.data) || readErrorCode(maybe?.data) || readErrorCode(error);
  if (code && SAP_RECONNECT_ERROR_CODES.has(code)) return code;
  const message = readErrorMessage(maybe?.response?.data) || readErrorMessage(maybe?.message);
  if (maybe?.response?.status === 409 && /SAP Datasphere|SAP session|refresh token/i.test(message ?? '')) {
    return 'SAP_REFRESH_FAILED';
  }
  return null;
}

export function isPreCannedSapReconnectError(error: unknown): boolean {
  return Boolean(getPreCannedSapReconnectErrorCode(error));
}

function throwIfSapReconnectError(error: unknown): never {
  const code = getPreCannedSapReconnectErrorCode(error);
  if (code) {
    throw new PreCannedSapReconnectRequiredError(
      code,
      readErrorMessage((error as { response?: { data?: unknown } })?.response?.data) ?? undefined,
    );
  }
  throw error;
}

const MONTHS: Record<string, number> = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

const calendarMonthToFiscalPeriod = (year: number, month: number) => {
  const fiscalYear = month >= 4 ? year + 1 : year;
  const fiscalPeriod = month >= 4 ? month - 3 : month + 9;
  return `${fiscalYear}${String(fiscalPeriod).padStart(3, '0')}`;
};

const fiscalTokenFromText = (value?: unknown): string | null => {
  const text = String(value ?? '');
  const rangeMatch = text.match(/FY\s*(\d{2,4})\s*P\s*\d{1,2}\s*-\s*P?\s*(\d{1,2})/i);
  if (rangeMatch) {
    const year = Number(rangeMatch[1]);
    const fiscalYear = year < 100 ? year + 2000 : year;
    const period = Number(rangeMatch[2]);
    if (period >= 1 && period <= 12) return `${fiscalYear}${String(period).padStart(3, '0')}`;
  }
  const matches = [...text.matchAll(/FY\s*(\d{2,4})\s*P\s*(\d{1,2})/gi)];
  const match = matches[matches.length - 1];
  if (!match) return null;
  const year = Number(match[1]);
  const fiscalYear = year < 100 ? year + 2000 : year;
  const period = Number(match[2]);
  if (period < 1 || period > 12) return null;
  return `${fiscalYear}${String(period).padStart(3, '0')}`;
};

const fiscalPeriodFromCalendarText = (value?: unknown): string | null => {
  const text = String(value ?? '');
  const rangeMatch = text.match(/(?:through|thru|to|-)\s*([A-Za-z]+)\s+(\d{4})/i);
  const match = rangeMatch ?? text.match(/\b([A-Za-z]+)\s+(\d{4})\b/);
  if (!match) return null;
  const month = MONTHS[String(match[1]).toLowerCase()];
  if (!month) return null;
  return calendarMonthToFiscalPeriod(Number(match[2]), month);
};

const fiscalPeriodFromPeriodValue = (value?: unknown): string | null => {
  const raw = String(value ?? '').trim().split('..').pop() ?? '';
  const match = raw.match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  return calendarMonthToFiscalPeriod(Number(match[1]), Number(match[2]));
};

export const deriveInventoryFiscalPeriod = (parameters: Record<string, unknown>): string | null => {
  const existing = String(parameters.inventory_fiscal_period ?? '').trim();
  if (existing) return existing;
  const fromLabel = fiscalTokenFromText(parameters.period_label);
  if (fromLabel) return fromLabel;
  const fromToolPeriod = fiscalTokenFromText(parameters.tool_period);
  if (fromToolPeriod) return fromToolPeriod;
  const fromToolCalendar = fiscalPeriodFromCalendarText(parameters.tool_period);
  if (fromToolCalendar) return fromToolCalendar;
  const fromPeriod = fiscalPeriodFromPeriodValue(parameters.period);
  if (fromPeriod) return fromPeriod;

  return null;
};

export const withInventoryFiscalPeriod = <T extends Record<string, unknown>>(parameters: T): T & { inventory_fiscal_period?: string } => {
  const inventoryFiscalPeriod = deriveInventoryFiscalPeriod(parameters);
  if (!inventoryFiscalPeriod) return parameters;
  return {
    ...parameters,
    inventory_fiscal_period: inventoryFiscalPeriod,
  };
};

export const smartMorningChatApi = {
  answer: (
    question: string,
    parameters: Record<string, unknown>,
    continuation: { thread_id?: string; run_id?: string; resume?: Record<string, string> } = {},
  ) => api.post<SmartMorningChatResponse>(`${BASE}/chat`, {
    question,
    parameters,
    ...continuation,
  }),
};

const delay = (ms: number) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const withHydrationMetadataFallback = (
  result: PreCannedReportResult | null | undefined,
  completedAt?: string | null,
): PreCannedReportResult => {
  const next: PreCannedReportResult = { ...(result ?? {}) };
  const meta: PreCannedReportMetadata = {
    ...(next.meta ?? {}),
  };
  if (!meta.last_updated_at && completedAt) {
    meta.last_updated_at = completedAt;
  }
  next.meta = meta;
  return next;
};

const waitForHydrationJob = async (runId: string, onProgress?: HydrationProgressHandler): Promise<PreCannedReportResult> => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < HYDRATION_MAX_WAIT_MS) {
    const { data } = await api.get<PreCannedHydrationJobStatus>(
      `${BASE}/hydration-jobs/${encodeURIComponent(runId)}`,
    );
    const status = String(data?.status ?? '').toUpperCase();
    if (data?.result && onProgress) onProgress(data.result);
    if (status === 'COMPLETED') return withHydrationMetadataFallback(data?.result, data?.completed_at);
    if (status === 'FAILED') {
      const code = readErrorCode(data);
      if (code && SAP_RECONNECT_ERROR_CODES.has(code)) {
        throw new PreCannedSapReconnectRequiredError(code, readErrorMessage(data) ?? undefined);
      }
      throw new Error(data?.error || 'Report hydration failed');
    }
    await delay(HYDRATION_POLL_INTERVAL_MS);
  }
  throw new Error('Report hydration is still running. Try refreshing the dashboard in a few minutes.');
};

const resolveHydrationJobResponse = (data: PreCannedHydrationJobCreated | null | undefined): PreCannedReportResult | null => {
  const status = String(data?.status ?? '').toUpperCase();
  if (status !== 'COMPLETED') return null;
  return withHydrationMetadataFallback(data?.result, data?.completed_at);
};

export const preCannedReportsApi = {
  async listTemplates(): Promise<PreCannedReportTemplateSummary[]> {
    const { data } = await api.get<PreCannedReportListResponse>(`${BASE}/templates`);
    return data?.templates ?? [];
  },

  async getTemplate(templateId: string): Promise<PreCannedReportTemplate> {
    const { data } = await api.get<PreCannedReportTemplate>(
      `${BASE}/templates/${encodeURIComponent(templateId)}`,
    );
    return data ?? {};
  },

  async getAuthoringTemplate(templateId: string): Promise<PreCannedReportTemplate> {
    const { data } = await api.get<PreCannedReportTemplate>(
      `${BASE}/templates/${encodeURIComponent(templateId)}/authoring`,
    );
    return data ?? {};
  },

  async hydrateTemplate(
    templateId: string,
    parameters: Record<string, unknown>,
    options: PreCannedHydrationOptions = {},
    onProgress?: HydrationProgressHandler,
  ): Promise<PreCannedReportResult> {
    let data: PreCannedHydrationJobCreated;
    try {
      ({ data } = await api.post<PreCannedHydrationJobCreated>(
        `${BASE}/${encodeURIComponent(templateId)}/hydrate/jobs`,
        { parameters, force_refresh: Boolean(options.forceRefresh) },
      ));
    } catch (error) {
      throwIfSapReconnectError(error);
    }
    const completedResult = resolveHydrationJobResponse(data);
    if (completedResult) return completedResult;
    if (!data?.run_id) throw new Error('Report hydration job was not created');
    return waitForHydrationJob(data.run_id, onProgress);
  },

  async previewTemplate(
    template: PreCannedReportTemplate,
    parameters: Record<string, unknown>,
    options: PreCannedHydrationOptions = {},
    onProgress?: HydrationProgressHandler,
  ): Promise<PreCannedReportResult> {
    let data: PreCannedHydrationJobCreated;
    try {
      ({ data } = await api.post<PreCannedHydrationJobCreated>(
        `${BASE}/preview/hydrate/jobs`,
        { template, parameters, force_refresh: Boolean(options.forceRefresh) },
      ));
    } catch (error) {
      throwIfSapReconnectError(error);
    }
    const completedResult = resolveHydrationJobResponse(data);
    if (completedResult) return completedResult;
    if (!data?.run_id) throw new Error('Report hydration job was not created');
    return waitForHydrationJob(data.run_id, onProgress);
  },

  async hydrateTemplateSync(
    templateId: string,
    parameters: Record<string, unknown>,
    options: PreCannedHydrationOptions = {},
  ): Promise<PreCannedReportResult> {
    let data: PreCannedReportResult;
    try {
      ({ data } = await api.post<PreCannedReportResult>(
        `${BASE}/${encodeURIComponent(templateId)}/hydrate`,
        { parameters, force_refresh: Boolean(options.forceRefresh) },
      ));
    } catch (error) {
      throwIfSapReconnectError(error);
    }
    return data ?? {};
  },

  async previewTemplateSync(
    template: PreCannedReportTemplate,
    parameters: Record<string, unknown>,
    options: PreCannedHydrationOptions = {},
  ): Promise<PreCannedReportResult> {
    let data: PreCannedReportResult;
    try {
      ({ data } = await api.post<PreCannedReportResult>(
        `${BASE}/preview/hydrate`,
        { template, parameters, force_refresh: Boolean(options.forceRefresh) },
      ));
    } catch (error) {
      throwIfSapReconnectError(error);
    }
    return data ?? {};
  },

  async resolvePeriod(period: string): Promise<PreCannedPeriodResolution> {
    const { data } = await api.post<PreCannedPeriodResolution>(
      `${BASE}/period/resolve`,
      { period },
    );
    return data;
  },

  async submitDashboardFeedback(
    request: PreCannedDashboardFeedbackRequest,
  ): Promise<PreCannedDashboardFeedbackResult> {
    const templateId = String(request.templateId || '').trim();
    const dashboardId = String(request.dashboardId || '').trim();
    if (!templateId || !dashboardId || !request.parameters) {
      return { status: 'skipped', reason: 'invalid_request' };
    }
    const text = String(request.text ?? '').trim();
    if (!text) return { status: 'skipped', reason: 'missing_text' };
    if (text.length > MAX_DASHBOARD_FEEDBACK_TEXT_LENGTH) {
      return { status: 'skipped', reason: 'text_too_long' };
    }

    try {
      const { data } = await api.post<PreCannedDashboardFeedbackResponse>(
        `${BASE}/${encodeURIComponent(templateId)}/feedback`,
        {
          dashboard_id: dashboardId,
          parameters: request.parameters,
          rating: request.rating,
          text,
        },
      );
      return { status: 'submitted', feedback: data.feedback };
    } catch (error) {
      return { status: 'failed', error };
    }
  },
};
